import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { sharingCalls } from '@/services/case-sharing';
import { ClientTeamMember, ShareAnchor } from '@/types/report-request';

/**
 * Client-side members of a case's team — the sharing picker's option
 * pool, resolved through the case's anchor (enquiry, request or report).
 * `enabled` gates the fetch (the picker loads lazily on first open; the
 * Backoffice bridge shouldn't be hit on every page view). A failed
 * lookup leaves `error` set and the caller degrades to free-text email
 * entry.
 */
export function useCaseTeamMembers(anchor: ShareAnchor | undefined, enabled: boolean) {
  const kind = anchor?.kind;
  const anchorId = anchor?.id;
  const [members, setMembers] = useState<ClientTeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!kind || !anchorId || !enabled || loaded) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    sharingCalls(kind)
      .teamMembers(anchorId)
      .then((rows) => {
        if (!cancelled) {
          setMembers(rows);
          setLoaded(true);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const e = err instanceof Error ? err : new Error('Failed to load team members');
          Sentry.captureException(e);
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [kind, anchorId, enabled, loaded]);

  return { members, isLoading, error };
}
